import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, PlusCircle, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { ROLES } from "../../lib/roles";

const TIPO_LABEL = {
    familiar_en_contraparte: "Familiar en contraparte",
    participacion_accionaria: "Participación accionaria",
    cargo_previo: "Cargo previo",
    otro: "Otro",
};

export default function Conflictos() {
    const { hasRole, user } = useAuth();
    const canReview = hasRole([
        ROLES.CONSEJO, ROLES.DIRECCION, ROLES.ADMIN_TI,
        ROLES.AUDITOR, ROLES.CUMPLIMIENTO,
    ]);
    const [rows, setRows] = useState([]);
    const [openDeclarar, setOpenDeclarar] = useState(false);
    const [form, setForm] = useState({
        tipo: "cargo_previo", descripcion: "", contraparte_referenciada: "",
        fideicomiso_relacionado: "", medida_mitigacion: "",
    });

    const load = async () => {
        if (!canReview) return;
        try {
            const { data } = await api.get("/gobierno/conflictos");
            setRows(data);
        } catch (err) {
            toast.error("No se pudo cargar", {
                description: err?.response?.data?.detail,
            });
        }
    };

    useEffect(() => { load(); }, [canReview]);

    const declarar = async () => {
        try {
            await api.post("/gobierno/conflictos", {
                ...form,
                contraparte_referenciada: form.contraparte_referenciada || null,
                fideicomiso_relacionado: form.fideicomiso_relacionado || null,
            });
            toast.success("Conflicto declarado");
            setOpenDeclarar(false);
            setForm({
                tipo: "cargo_previo", descripcion: "", contraparte_referenciada: "",
                fideicomiso_relacionado: "", medida_mitigacion: "",
            });
            load();
        } catch (err) {
            toast.error("No se pudo declarar", {
                description: err?.response?.data?.detail,
            });
        }
    };

    const cerrar = async (id) => {
        try {
            await api.post(`/gobierno/conflictos/${id}/cerrar`);
            toast.success("Conflicto cerrado");
            load();
        } catch (err) {
            toast.error("No se pudo cerrar", {
                description: err?.response?.data?.detail,
            });
        }
    };

    return (
        <div className="p-8 lg:p-10 space-y-6 max-w-[1500px]" data-testid="conflictos-page">
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Gobierno · Fase 14
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <AlertTriangle className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Conflictos de Interés
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Auto-declaración con medidas de mitigación. Todos los conflictos vigentes
                        se incluyen en el reporte trimestral al Consejo.
                    </p>
                </div>
                <Button onClick={() => setOpenDeclarar(true)} data-testid="declarar-conflicto">
                    <PlusCircle className="h-3.5 w-3.5 mr-2" /> Declarar conflicto
                </Button>
            </div>

            {canReview ? (
                <div className="rounded-md border border-border overflow-hidden bg-card">
                    <div className="overflow-x-auto">
                        <table className="trf-table" data-testid="conflictos-table">
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Usuario</th>
                                    <th>Tipo</th>
                                    <th>Descripción</th>
                                    <th>Contraparte</th>
                                    <th>Mitigación</th>
                                    <th>Estado</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((c) => (
                                    <tr key={c.id} data-testid={`conflicto-row-${c.id}`}>
                                        <td className="tabular text-xs">{c.fecha_declaracion?.slice(0, 10)}</td>
                                        <td className="text-xs">{c.usuario_email}</td>
                                        <td>
                                            <span className="badge-gold text-[10px]">
                                                {TIPO_LABEL[c.tipo] || c.tipo}
                                            </span>
                                        </td>
                                        <td className="text-xs max-w-xs">{c.descripcion}</td>
                                        <td className="text-xs">{c.contraparte_referenciada || "—"}</td>
                                        <td className="text-xs">{c.medida_mitigacion}</td>
                                        <td>
                                            {c.vigente ? (
                                                <span className="text-[11px] uppercase tracking-widest border rounded-md px-2 py-1 text-amber-700 bg-amber-50 border-amber-200">
                                                    Vigente
                                                </span>
                                            ) : (
                                                <span className="text-[11px] uppercase tracking-widest border rounded-md px-2 py-1 text-emerald-700 bg-emerald-50 border-emerald-200">
                                                    Cerrado
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {c.vigente && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => cerrar(c.id)}
                                                    data-testid={`cerrar-${c.id}`}
                                                >
                                                    <CheckCircle2 className="h-3 w-3 mr-1" /> Cerrar
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {rows.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="text-center py-10 text-muted-foreground">
                                            Sin conflictos registrados.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="kpi-card text-sm text-muted-foreground">
                    Sólo el Consejo, Dirección, Cumplimiento, Auditor y Admin TI pueden ver el listado.
                    Puedes declarar tus propios conflictos con el botón superior.
                </div>
            )}

            <Dialog open={openDeclarar} onOpenChange={setOpenDeclarar}>
                <DialogContent className="max-w-2xl" data-testid="declarar-dialog">
                    <DialogHeader><DialogTitle>Declarar conflicto de interés</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                Tipo
                            </label>
                            <Select
                                value={form.tipo}
                                onValueChange={(v) => setForm({ ...form, tipo: v })}
                            >
                                <SelectTrigger data-testid="conflicto-tipo"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {Object.entries(TIPO_LABEL).map(([v, l]) => (
                                        <SelectItem key={v} value={v}>{l}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                Descripción (mínimo 5 caracteres)
                            </label>
                            <Textarea
                                value={form.descripcion}
                                onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                                rows={3}
                                data-testid="conflicto-descripcion"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                    Contraparte (opcional)
                                </label>
                                <Input
                                    value={form.contraparte_referenciada}
                                    onChange={(e) => setForm({ ...form, contraparte_referenciada: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                    Fideicomiso relacionado
                                </label>
                                <Input
                                    value={form.fideicomiso_relacionado}
                                    onChange={(e) => setForm({ ...form, fideicomiso_relacionado: e.target.value })}
                                    placeholder="ID o TRF-2026-####"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                Medida de mitigación
                            </label>
                            <Textarea
                                value={form.medida_mitigacion}
                                onChange={(e) => setForm({ ...form, medida_mitigacion: e.target.value })}
                                rows={2}
                                data-testid="conflicto-mitigacion"
                                placeholder="Ej. abstención de firma en operaciones relacionadas"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpenDeclarar(false)}>Cancelar</Button>
                        <Button onClick={declarar} data-testid="conflicto-submit">Declarar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
